import { useState } from "react";
import {
  ArrowLeft, ArrowRight, Check, Loader2,
  Users, Shield, CreditCard, BookOpen, Bell, BarChart2,
  Activity, Workflow
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import AnalyticsChart from "../components/charts/AnalyticsChart";

const DATA_SOURCES = [
  { key: "requests", label: "Access & Requests", icon: Users, description: "Visitor and contractor access requests" },
  { key: "approvals", label: "Approvals", icon: Check, description: "Approval workflows and decisions" },
  { key: "checkpoints", label: "Checkpoint Operations", icon: Shield, description: "Entry/exit checkpoint events" },
  { key: "security", label: "Security & Alerts", icon: Bell, description: "Security alerts and incidents" },
  { key: "cards", label: "Card Management", icon: CreditCard, description: "Access card issuance and lifecycle" },
  { key: "knowledge", label: "Knowledge & Compliance", icon: BookOpen, description: "Policy acknowledgements and training" },
  { key: "users", label: "Users", icon: Users, description: "User accounts and profiles" },
  { key: "workflows", label: "Workflows", icon: Activity, description: "Workflow definitions and executions" },
];

const CHART_TYPES = [
  { key: "table", label: "Table" },
  { key: "bar", label: "Bar Chart" },
  { key: "h-bar", label: "Horizontal Bar" },
  { key: "line", label: "Line Chart" },
  { key: "area", label: "Area Chart" },
  { key: "pie", label: "Pie Chart" },
  { key: "donut", label: "Donut Chart" },
  { key: "scatter", label: "Scatter Plot" },
  { key: "gauge", label: "Gauge" },
  { key: "heatmap", label: "Heatmap" },
];

const FIELDS_BY_SOURCE: Record<string, string[]> = {
  requests: ["id", "status", "requestType", "createdAt", "siteId", "requestorId", "approvedAt"],
  approvals: ["id", "status", "approverId", "stage", "approvedAt", "requestId"],
  checkpoints: ["id", "entryTime", "exitTime", "status", "siteId", "userId", "method"],
  security: ["id", "alertType", "severity", "status", "siteId", "createdAt"],
  cards: ["id", "cardNumber", "accessLevel", "status", "expiryDate", "userId"],
  knowledge: ["id", "contentType", "category", "acknowledgedAt", "userId", "completionRate"],
  users: ["id", "name", "email", "role", "department", "status", "createdAt"],
  workflows: ["id", "name", "status", "triggeredAt", "completedAt", "requestId"],
};

const OPERATORS = ["equals", "not equals", "greater than", "less than", "contains", "is empty", "is not empty"];

interface ReportBuilderProps {
  onBack: () => void;
  onSaved: () => void;
}

export default function ReportBuilder({ onBack, onSaved }: ReportBuilderProps) {
  const [step, setStep] = useState(1);
  const [reportName, setReportName] = useState("");
  const [selectedSource, setSelectedSource] = useState("");
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [filters, setFilters] = useState<{ field: string; operator: string; value: string }[]>([]);
  const [chartType, setChartType] = useState("bar");

  const createMutation = trpc.analytics.reports.create.useMutation({
    onSuccess: () => {
      toast.success("Report saved successfully.");
      onSaved();
    },
    onError: () => toast.error("Failed to save report."),
  });

  const availableFields = FIELDS_BY_SOURCE[selectedSource] ?? [];

  const toggleField = (field: string) => {
    setSelectedFields((prev) =>
      prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]
    );
  };

  const addFilter = () => {
    if (availableFields.length === 0) return;
    setFilters((prev) => [...prev, { field: availableFields[0], operator: "equals", value: "" }]);
  };

  const removeFilter = (i: number) => setFilters((prev) => prev.filter((_, idx) => idx !== i));

  const updateFilter = (i: number, key: string, value: string) => {
    setFilters((prev) => prev.map((f, idx) => (idx === i ? { ...f, [key]: value } : f)));
  };

  const handleSave = () => {
    if (!reportName.trim()) {
      toast.error("Please enter a report name.");
      return;
    }
    createMutation.mutate({
      name: reportName,
      dataSource: selectedSource,
      definition: {
        fields: selectedFields,
        filters,
        chartType,
        chartConfig: {},
      },
    });
  };

  const STEPS = [
    { num: 1, label: "Data Source" },
    { num: 2, label: "Fields" },
    { num: 3, label: "Filters" },
    { num: 4, label: "Chart Type" },
    { num: 5, label: "Preview & Save" },
  ];

  const canNext = () => {
    if (step === 1) return !!selectedSource;
    if (step === 2) return selectedFields.length > 0;
    if (step === 5) return !!reportName.trim();
    return true;
  };

  return (
    <div className="flex flex-col min-h-full">
      {/* Header */}
      <div className="flex items-center gap-3 px-6 py-4 border-b bg-card sticky top-0 z-10">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <div className="h-5 w-px bg-border" />
        <h2 className="font-semibold text-base text-foreground">Report Builder</h2>
      </div>

      {/* Step Indicator */}
      <div className="px-6 py-4 border-b bg-card sticky top-[65px] z-10">
        <div className="flex items-center gap-0">
          {STEPS.map((s, i) => (
            <div key={s.num} className="flex items-center">
              <div className="flex items-center gap-2">
                <div
                  className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold transition-colors ${
                    step > s.num
                      ? "bg-primary text-primary-foreground"
                      : step === s.num
                      ? "bg-primary text-primary-foreground ring-2 ring-primary/30"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {step > s.num ? <Check className="w-3.5 h-3.5" /> : s.num}
                </div>
                <span
                  className={`text-xs font-medium hidden sm:block ${
                    step >= s.num ? "text-foreground" : "text-muted-foreground"
                  }`}
                >
                  {s.label}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`w-8 h-px mx-2 ${step > s.num ? "bg-primary" : "bg-border"}`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="flex-1 p-6 pb-24">
        {/* Step 1: Data Source */}
        {step === 1 && (
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-base text-foreground mb-1">Choose a Data Source</h3>
              <p className="text-sm text-muted-foreground">Select the Centre3 data domain for this report.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {DATA_SOURCES.map((ds) => {
                const Icon = ds.icon;
                const isSelected = selectedSource === ds.key;
                return (
                  <button
                    key={ds.key}
                    onClick={() => setSelectedSource(ds.key)}
                    className={`text-left rounded-xl border p-4 transition-all ${
                      isSelected
                        ? "border-primary bg-primary/5 ring-1 ring-primary"
                        : "border-border bg-card hover:border-primary/40"
                    }`}
                  >
                    <div className={`flex items-center justify-center w-9 h-9 rounded-lg mb-3 ${isSelected ? "bg-primary/10" : "bg-muted"}`}>
                      <Icon className={`w-5 h-5 ${isSelected ? "text-primary" : "text-muted-foreground"}`} />
                    </div>
                    <p className="font-medium text-sm text-foreground">{ds.label}</p>
                    <p className="text-xs text-muted-foreground mt-1">{ds.description}</p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 2: Fields */}
        {step === 2 && (
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-base text-foreground mb-1">Select Fields</h3>
              <p className="text-sm text-muted-foreground">Choose which fields to include in your report.</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {availableFields.map((field) => {
                const isSelected = selectedFields.includes(field);
                return (
                  <button
                    key={field}
                    onClick={() => toggleField(field)}
                    className={`text-left rounded-lg border px-3 py-2 text-sm transition-all ${
                      isSelected
                        ? "border-primary bg-primary/5 text-primary font-medium"
                        : "border-border bg-card text-foreground hover:border-primary/40"
                    }`}
                  >
                    {isSelected && <Check className="w-3 h-3 inline mr-1.5" />}
                    {field}
                  </button>
                );
              })}
            </div>
            {selectedFields.length > 0 && (
              <div className="rounded-lg bg-muted/50 p-3">
                <p className="text-xs text-muted-foreground mb-2">Selected ({selectedFields.length}):</p>
                <div className="flex flex-wrap gap-1">
                  {selectedFields.map((f) => (
                    <Badge key={f} variant="secondary" className="text-xs">{f}</Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Filters */}
        {step === 3 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-base text-foreground mb-1">Add Filters</h3>
                <p className="text-sm text-muted-foreground">Narrow down the data with conditions (AND logic).</p>
              </div>
              <Button variant="outline" size="sm" onClick={addFilter}>
                + Add Filter
              </Button>
            </div>
            {filters.length === 0 ? (
              <div className="rounded-xl border border-dashed p-8 text-center">
                <p className="text-sm text-muted-foreground">No filters added. Click "Add Filter" to narrow results.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filters.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-lg border bg-card p-3">
                    <select
                      className="flex-1 rounded border border-input bg-background px-2 py-1 text-sm"
                      value={f.field}
                      onChange={(e) => updateFilter(i, "field", e.target.value)}
                    >
                      {availableFields.map((field) => (
                        <option key={field} value={field}>{field}</option>
                      ))}
                    </select>
                    <select
                      className="flex-1 rounded border border-input bg-background px-2 py-1 text-sm"
                      value={f.operator}
                      onChange={(e) => updateFilter(i, "operator", e.target.value)}
                    >
                      {OPERATORS.map((op) => (
                        <option key={op} value={op}>{op}</option>
                      ))}
                    </select>
                    <Input
                      className="flex-1 h-8 text-sm"
                      placeholder="Value"
                      value={f.value}
                      onChange={(e) => updateFilter(i, "value", e.target.value)}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                      onClick={() => removeFilter(i)}
                    >
                      ×
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 4: Chart Type */}
        {step === 4 && (
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-base text-foreground mb-1">Choose Chart Type</h3>
              <p className="text-sm text-muted-foreground">Select how to visualize your data.</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {CHART_TYPES.map((ct) => {
                const isSelected = chartType === ct.key;
                return (
                  <button
                    key={ct.key}
                    onClick={() => setChartType(ct.key)}
                    className={`rounded-xl border p-4 text-center transition-all ${
                      isSelected
                        ? "border-primary bg-primary/5 ring-1 ring-primary"
                        : "border-border bg-card hover:border-primary/40"
                    }`}
                  >
                    <BarChart2 className={`w-6 h-6 mx-auto mb-2 ${isSelected ? "text-primary" : "text-muted-foreground"}`} />
                    <p className={`text-xs font-medium ${isSelected ? "text-primary" : "text-foreground"}`}>{ct.label}</p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 5: Preview & Save */}
        {step === 5 && (
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-base text-foreground mb-1">Preview & Save</h3>
              <p className="text-sm text-muted-foreground">Name your report and review the configuration.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="report-name">Report Name *</Label>
              <Input
                id="report-name"
                placeholder="e.g. Monthly Approval Summary"
                value={reportName}
                onChange={(e) => setReportName(e.target.value)}
                className="max-w-md"
              />
            </div>
            <div className="rounded-xl border bg-card p-5">
              <h4 className="font-medium text-sm text-foreground mb-4">Chart Preview</h4>
              <AnalyticsChart
                chartType={chartType}
                data={{
                  xAxis: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
                  series: [{ name: "Sample Data", data: [42, 58, 35, 71, 63, 88] }],
                }}
                height={280}
              />
            </div>
            <div className="rounded-xl border bg-card p-4 space-y-2">
              <h4 className="font-medium text-sm text-foreground">Configuration Summary</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Data Source: </span>
                  <span className="font-medium">{selectedSource}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Chart Type: </span>
                  <span className="font-medium capitalize">{chartType}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Fields: </span>
                  <span className="font-medium">{selectedFields.length} selected</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Filters: </span>
                  <span className="font-medium">{filters.length} conditions</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Navigation Footer */}
      <div className="flex items-center justify-between px-6 py-4 border-t bg-card sticky bottom-0 z-10">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setStep((s) => Math.max(1, s - 1))}
          disabled={step === 1}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Previous
        </Button>
        <span className="text-xs text-muted-foreground">Step {step} of 5</span>
        {step < 5 ? (
          <Button
            size="sm"
            onClick={() => setStep((s) => Math.min(5, s + 1))}
            disabled={!canNext()}
          >
            Next
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button size="sm" onClick={handleSave} disabled={createMutation.isPending || !canNext()}>
            {createMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save Report
          </Button>
        )}
      </div>
    </div>
  );
}
